/** Placement-only presentation. Normal settlement painter order remains authoritative. */
export interface SettlementEditorFocusBuilding {
  id: string;
  depth: number;
  /** Full visible sprite bounds, all expressed in the same coordinate space. */
  bounds: { left: number; top: number; right: number; bottom: number };
}

export interface SettlementEditorFocusStyle {
  alpha: number;
  depth: number;
}

export const SETTLEMENT_EDITOR_OCCLUDER_ALPHA = 0.28;

/**
 * Apply only while previewing a selected placement. Uses original depths to find
 * foreground occluders, then lifts the selected sprite above the normal scene.
 * Boundary contact alone is not occlusion. No selection restores ordinary styles.
 */
export function planSettlementEditorFocus(
  buildings: readonly SettlementEditorFocusBuilding[],
  selectedId?: string | null,
): ReadonlyMap<string, SettlementEditorFocusStyle> {
  const styles = new Map<string, SettlementEditorFocusStyle>();
  let maxDepth = -Infinity;
  for (const building of buildings) {
    const { left, top, right, bottom } = building.bounds;
    if (
      building.id.length === 0 ||
      styles.has(building.id) ||
      ![building.depth, left, top, right, bottom].every(Number.isFinite) ||
      left > right ||
      top > bottom
    )
      throw new RangeError('Editor focus requires unique IDs, finite depths and ordered bounds.');
    styles.set(building.id, { alpha: 1, depth: building.depth });
    maxDepth = Math.max(maxDepth, building.depth);
  }
  const selected = buildings.find((building) => building.id === selectedId);
  if (!selected) return styles;

  // A relative increment remains representable for large but finite painter depths.
  const selectedDepth = maxDepth + Math.max(1, Math.abs(maxDepth) * Number.EPSILON * 2);
  if (!Number.isFinite(selectedDepth) || selectedDepth <= maxDepth)
    throw new RangeError('Editor focus cannot represent a finite depth above this scene.');
  styles.set(selected.id, { alpha: 1, depth: selectedDepth });
  for (const building of buildings) {
    if (building.id === selected.id || building.depth <= selected.depth) continue;
    const a = selected.bounds;
    const b = building.bounds;
    const overlapWidth = Math.min(a.right, b.right) - Math.max(a.left, b.left);
    const overlapHeight = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
    if (overlapWidth > 0 && overlapHeight > 0)
      styles.set(building.id, { alpha: SETTLEMENT_EDITOR_OCCLUDER_ALPHA, depth: building.depth });
  }
  return styles;
}
